import { type ReactNode } from "react"
import { Badge, Box, Text } from "@chakra-ui/react"
import { Chip, chipStyle } from "./Chip"
import { FiExternalLink } from "react-icons/fi"
import { useNavigate } from "react-router-dom"
import { useNavigation } from "../providers/NavigationProvider"

// Entity scope palettes from docs/architecture/ui-visual-vocabulary.md §1C
export const SCOPE_CHIP_COLOURS = {
  asset: { bg: "#EBF8FF", color: "#2B6CB0", hover: "#BEE3F8" },
  system: { bg: "#EDF2F7", color: "#4A5568", hover: "#E2E8F0" },
  neutral: { bg: "#EDF2F7", color: "#4A5568", hover: "#E2E8F0" },
}

export interface ScopeLinkChipProps {
  /** Chip text, e.g. "Asset" or "System". */
  label: string
  /** Route the chip links to, e.g. /assets/:id/settings?tab=connections. */
  href: string
  /** Name of the entity, used for accessible labels. */
  entityName: string
  colours?: { bg: string; color: string; hover: string }
}

/**
 * Multi-action scope chip (ui-visual-vocabulary.md §4): the text navigates in the current tab,
 * the icon opens a new tab. Both stop propagation so the chip is safe inside clickable rows.
 */
export function ScopeLinkChip({ label, href, entityName, colours = SCOPE_CHIP_COLOURS.asset }: ScopeLinkChipProps) {
  const hubNavigate = useNavigation()
  const navigate = useNavigate()

  return (
    <Badge {...chipStyle(colours)}>
      <Text
        as="span"
        cursor="pointer"
        _hover={{ textDecoration: "underline", color: "#1A365D" }}
        onClick={(e) => {
          e.stopPropagation()
          ;(hubNavigate ?? navigate)(href)
        }}
        aria-label={`Go to ${entityName}`}
      >
        {label}
      </Text>
      <Box
        as="button"
        display="inline-flex"
        alignItems="center"
        cursor="pointer"
        p="3px"
        ml="2px"
        borderRadius="xs"
        _hover={{ bg: colours.hover, color: "#1A365D" }}
        onClick={(e) => {
          e.stopPropagation()
          window.open(href, "_blank", "noopener,noreferrer")
        }}
        aria-label={`Open ${entityName} in new tab`}
      >
        <FiExternalLink size="11px" />
      </Box>
    </Badge>
  )
}

/** A plain chip in a scope's colours, for labels that link nowhere. */
export function ScopeChip({ children, colours = SCOPE_CHIP_COLOURS.neutral }: { children: ReactNode; colours?: ScopeLinkChipProps["colours"] }) {
  return <Chip colours={colours ?? SCOPE_CHIP_COLOURS.neutral}>{children}</Chip>
}

export default ScopeLinkChip
